import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicManager } from 'managers/topic';
import WCCAudio from 'models/attachments/audio';
import WCCFile from 'models/attachments/file';
import WCCImage from 'models/attachments/image';
import WCCVideo from 'models/attachments/video';
import { TopicThread } from 'models/topicThread';
import { ServicesContext } from 'services/context';
import './body.html';
import './body.scss';

components.preload('photoset-new', 'grid-new', 'wcc-file', 'wcc-video', 'wcc-audio', 'wcc-heatmap', 'attachment-controls', 'wcc-list', 'attachment-transcript');

export interface CommentBodyVMConfig {
    comment: SubscribableOrNullableValue<TopicThread>
    onlyContent: SubscribableOrNullableValue<boolean>
    compact: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class CommentBodyVM {
    labels = labels    

    comment: Subscribable<TopicThread | undefined>
    commentId: Subscribable<string | undefined>
    topicId: Subscribable<string | undefined>
    videoTemplate: Subscribable<string>

    hasImages: Subscribable<boolean>
    hasAudios: Subscribable<boolean>
    hasFiles: Subscribable<boolean>
    hasAttachments: Subscribable<boolean>
    heatmapPinShown: Subscribable<boolean>
    isCompact: Subscribable<boolean>
    isTranscriptAllowed: Subscribable<boolean>

    images: Subscribable<Array<WCCImage>>
    files: Subscribable<Array<WCCFile>>
    videos: Subscribable<Array<WCCVideo>>
    audios: Subscribable<Array<WCCAudio>>
    pins: Subscribable<Array<TopicThread>>   

    constructor(
        @inject(wccModules.componentConfig) config: CommentBodyVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.servicesContext) private ctx: ServicesContext
    ) {
        this.comment = ko.flattenComputed(config.comment);

        this.commentId = this.comment.pluck('id');
        this.topicId = this.comment.pluck('topicId');
        this.videoTemplate = ko.pureComputed(() => this.getVideoTemplate());

        const onlyContent = ko.flattenComputed(config.onlyContent, false);
        this.isCompact = ko.flattenComputed(config.compact, false);

        const userManager = storage.get(CurrentUserManager, {});
        const user = userManager.pluck(m => m.person);
        const isModerator = user.pluck(u => u.isModerator, false);
        this.isTranscriptAllowed = ko.pureComputed(() => isModerator());

        const topicManager = storage.get(TopicManager, { topicId: this.topicId });
        const topic = topicManager.pluck('topic');
        const isHeatMapPin = topic.pluck('isHeatMap', false);

        this.heatmapPinShown = ko.pureComputed(() => !onlyContent() && isHeatMapPin() && this.pins().length > 0);     

        const attachments = this.comment.pluck(c => c.attachments, []);
        this.images = attachments.filter<WCCImage>(a => a.fileType() == enums.AttachmentTypes.Image.value);
        this.videos = attachments.filter<WCCVideo>(a => a.fileType() == enums.AttachmentTypes.Video.value);
        this.audios = attachments.filter<WCCAudio>(a => a.fileType() == enums.AttachmentTypes.Audio.value);
        this.files = attachments.filter<WCCFile>(a => a.fileType() == enums.AttachmentTypes.File.value);

        this.pins = ko.pureComputed(() => this.getPins());

        this.hasImages = this.images.any();
        this.hasAudios = this.audios.any();
        this.hasFiles = this.files.any();
        const hasVideos = this.videos.any();
        this.hasAttachments = ko.pureComputed(() => this.hasImages() || hasVideos() || this.hasAudios() || this.hasFiles());

        ko.applyBindingsToNode(info.element, {
            'css': {
                'comment-body': true,
                'comment-body--compact': this.isCompact
            }
        });
    }

    async generateTranscript(video: any) {
        try {
            const commentId = this.comment()?.id();
            const videoId = video.attachmentId();

            if (commentId == undefined || videoId == undefined)
                throw new Error(messages.InvalidRequest);

            await this.ctx.attachmentsService.queries.generateTranscript(commentId, videoId);
        } catch (ex) {
            system.handleError(ex);
        }
    }

    private getPins() {
        const comment = this.comment();

        if (comment != undefined && comment.point() != undefined)
            return [comment];

        return [];
    }

    private getVideoTemplate() {
        //const user = this.user();
        //const isModerator = user?.isModerator() ?? false;

        //if (isModerator)
        //    return 'wcc-components-comment-content-video-with-tools';

        return 'wcc-components-comment-content-video';
    }
}